import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { FolderKanban, Search } from 'lucide-react';
import { MobileSubMenuHeader } from '@/components/layout';
import { navigationItems } from '@/lib/navigation';
import { projectService, type Project } from '@/lib/projectService';
import { ProjectCard } from '@/components/projects/ProjectCard';
import { CreateProjectDialog } from '@/components/projects/CreateProjectDialog';
import { useWorkspace } from '@/context/WorkspaceContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { X, ChevronDown, Filter, Plus } from 'lucide-react';
import { useRef } from 'react';

interface ProjectsProps {
  isGlobalDarkMode: boolean;
  onMobileSubMenuToggle?: (menuId: string | null, subItems?: any[], menuLabel?: string) => void;
  isMobile?: boolean;
}

const statusFilters = [
  { value: 'all', label: 'All Projects' },
  { value: 'active', label: 'Active' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const Projects: React.FC<ProjectsProps> = ({ isGlobalDarkMode, onMobileSubMenuToggle, isMobile = false }) => {
  const [mobileSearchQuery, setMobileSearchQuery] = React.useState('');
  const { currentWorkspace } = useWorkspace();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchOpen) {
      searchInputRef.current?.focus();
    }
  }, [searchOpen]);

  const handleMobileMenuToggle = () => {
    const crmMenuItem = navigationItems.find(item => item.id === 'crm');
    if (crmMenuItem && onMobileSubMenuToggle) {
      onMobileSubMenuToggle('crm', crmMenuItem.subItems, crmMenuItem.label);
    }
  };

  const loadProjects = useCallback(async () => {
    if (!currentWorkspace) return;
    try {
      setLoading(true);
      const data = await projectService.getProjectsByWorkspace(currentWorkspace.id);
      setProjects(data);
    } catch (error) {
      console.error('Error loading projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace?.id]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleDelete = async (project: Project) => {
    if (!confirm(`Are you sure you want to delete "${project.name}"? This will also delete the linked task board, files, and chat channel.`)) return;
    try {
      await projectService.deleteProject(project.id);
      toast.success('Project deleted');
      loadProjects();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const filteredProjects = projects.filter(p => {
    const matchesSearch = !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: projects.length,
    active: projects.filter(p => p.status === 'active').length,
    completed: projects.filter(p => p.status === 'completed').length,
    onHold: projects.filter(p => p.status === 'on_hold').length,
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Projects | AgencyPlus</title>
        <meta name="description" content="Manage your client projects" />
        <link rel="canonical" href="/boards/projects" />
      </Helmet>

      {isMobile && (
        <MobileSubMenuHeader
          onMenuClick={handleMobileMenuToggle}
          onAddClick={() => setCreateDialogOpen(true)}
          searchValue={mobileSearchQuery}
          onSearchChange={setMobileSearchQuery}
          searchPlaceholder="Search projects..."
          showSearch={true}
          showAdd={true}
          isDarkMode={isGlobalDarkMode}
        />
      )}

      <div className={`w-full px-6 md:px-10 py-6 ${isMobile ? 'pt-20' : ''}`}>
        {/* Unified Header */}
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Projects</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage your client projects and track progress
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* Search — collapsible */}
            <div className="relative flex items-center h-10">
              <AnimatePresence initial={false}>
                {searchOpen ? (
                  <motion.div
                    key="search-open"
                    className="relative flex items-center h-10"
                    initial={{ width: 40, opacity: 0.6 }}
                    animate={{ width: 256, opacity: 1 }}
                    exit={{ width: 40, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                  >
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search projects..."
                      onBlur={() => { if (!searchQuery) setSearchOpen(false); }}
                      className="w-full h-full pl-9 pr-8 text-sm rounded-lg border transition-colors bg-card border-border  focus:ring-1 focus:ring-workspace-accent/30"
                    />
                    <button
                      onClick={() => { setSearchQuery(''); setSearchOpen(false); }}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </motion.div>
                ) : (
                  <motion.button
                    key="search-icon"
                    onClick={() => setSearchOpen(true)}
                    className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border  hover:bg-muted transition-colors"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <Search className="w-4 h-4" />
                  </motion.button>
                )}
              </AnimatePresence>
            </div>

            {/* Filter Toggle */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={`flex items-center gap-2 px-4 h-10 rounded-lg border transition-colors whitespace-nowrap ${statusFilter !== 'all'
                    ? 'bg-workspace-accent text-white border-transparent'
                    : 'bg-card border-border  hover:bg-muted'
                  }`}>
                  <Filter className="w-4 h-4" />
                  <span className="text-sm font-medium">{statusFilter === 'all' ? 'Filter' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}</span>
                  <ChevronDown className="w-3.5 h-3.5 opacity-50" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-popover border-border">
                {statusFilters.map(f => (
                  <DropdownMenuItem key={f.value} onClick={() => setStatusFilter(f.value)} className="flex items-center justify-between">
                    {f.label} {statusFilter === f.value && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* New Project Button */}
            <button
              onClick={() => setCreateDialogOpen(true)}
              className="flex items-center gap-2 px-6 h-10 bg-workspace-accent text-white rounded-lg hover:bg-workspace-accent-hover transition-colors whitespace-nowrap"
            >
              <Plus className="w-5 h-5" />
              <span className="font-medium">New Project</span>
            </button>
          </div>
        </div>

        <CreateProjectDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          onProjectCreated={loadProjects}
          hideTrigger
        />

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="p-6 rounded-2xl bg-card border border-border shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Total</span>
              <FolderKanban className="w-5 h-5 text-workspace-accent opacity-20" />
            </div>
            <div className="text-3xl font-bold text-foreground">{stats.total}</div>
          </div>
          <div className="p-6 rounded-2xl bg-card border border-border shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Active</span>
              <div className="w-2 h-2 bg-emerald-500 rounded-full" />
            </div>
            <div className="text-3xl font-bold text-foreground">{stats.active}</div>
          </div>
          <div className="p-6 rounded-2xl bg-card border border-border shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Completed</span>
              <div className="w-2 h-2 bg-blue-500 rounded-full" />
            </div>
            <div className="text-3xl font-bold text-foreground">{stats.completed}</div>
          </div>
          <div className="p-6 rounded-2xl bg-card border border-border shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">On Hold</span>
              <div className="w-2 h-2 bg-amber-500 rounded-full" />
            </div>
            <div className="text-3xl font-bold text-foreground">{stats.onHold}</div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          null
        ) : filteredProjects.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map(project => (
              <ProjectCard
                key={project.id}
                project={project}
                onEdit={() => toast.info('Edit coming soon')}
                onDelete={handleDelete}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 bg-workspace-accent/10">
              <FolderKanban className="w-10 h-10 text-workspace-accent" />
            </div>
            <h2 className="text-xl font-bold mb-2 text-foreground">
              {searchQuery || statusFilter !== 'all' ? 'No projects found' : 'No projects yet'}
            </h2>
            <p className="text-center max-w-md mb-6 text-muted-foreground">
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Create your first project to get started. A task board, file folder, and team channel will be set up automatically.'}
            </p>
            {!searchQuery && statusFilter === 'all' && (
              <CreateProjectDialog onProjectCreated={loadProjects} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Projects;
